'use client';

import { PropsWithChildren, ReactNode } from 'react';
import * as Headless from '@headlessui/react';

import { useRTL } from '@northladder/i18n';
import { useAppConfigStore } from '@northladder/services';

import { cn } from '../lib';

import { NavbarItem } from './navbar';

function OpenMenuIcon() {
  return (
    <svg aria-hidden="true" data-slot="icon" viewBox="0 0 20 20">
      <path d="M2 6.75C2 6.33579 2.33579 6 2.75 6H17.25C17.6642 6 18 6.33579 18 6.75C18 7.16421 17.6642 7.5 17.25 7.5H2.75C2.33579 7.5 2 7.16421 2 6.75ZM2 13.25C2 12.8358 2.33579 12.5 2.75 12.5H17.25C17.6642 12.5 18 12.8358 18 13.25C18 13.6642 17.6642 14 17.25 14H2.75C2.33579 14 2 13.6642 2 13.25Z" />
    </svg>
  );
}

function CloseMenuIcon() {
  return (
    <svg aria-hidden="true" data-slot="icon" viewBox="0 0 20 20">
      <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
    </svg>
  );
}

function MobileSidebar({
  open,
  close,
  children,
}: PropsWithChildren<{ open: boolean; close: () => void }>) {
  const { isRTL } = useRTL();

  return (
    <Headless.Dialog className="lg:hidden" onClose={close} open={open}>
      <Headless.DialogBackdrop
        className="
          data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in
          fixed inset-0 bg-black/30
          transition
        "
        transition
      />
      <Headless.DialogPanel
        className={cn(
          'max-w-80 fixed inset-y-0 w-full p-2 transition duration-300 ease-in-out',
          isRTL
            ? 'data-closed:translate-x-full'
            : 'data-closed:-translate-x-full'
        )}
        transition
      >
        <div
          className="
            shadow-xs flex h-full flex-col rounded-lg bg-white ring-1
            ring-gray-950/5 dark:bg-gray-900 dark:ring-white/10
          "
        >
          <div className="-mb-3 px-4 pt-3">
            <Headless.CloseButton aria-label="Close navigation" as={NavbarItem}>
              <CloseMenuIcon />
            </Headless.CloseButton>
          </div>
          {children}
        </div>
      </Headless.DialogPanel>
    </Headless.Dialog>
  );
}

/**
 * A layout component that handles the overall page structure with a sidebar and navbar.
 */
export function SidebarLayout({
  navbar,
  sidebar,
  children,
  headerChildren,
}: PropsWithChildren<{
  /** The navbar component to display. */
  navbar: ReactNode;
  /** The sidebar component to display. */
  sidebar: ReactNode;
  /** Additional content to display in the header area. */
  headerChildren: ReactNode;
}>) {
  const { isSidebarExpanded, setIsSidebarExpanded } = useAppConfigStore();
  const { isRTL } = useRTL();

  const handleToggleSidebar = () => {
    setIsSidebarExpanded(!isSidebarExpanded);
  };

  return (
    <div
      className="
          min-h-svh relative isolate flex w-full bg-white dark:bg-gray-500
          dark:bg-gray-950 max-lg:flex-col lg:bg-gray-100
          dark:lg:bg-gray-900 dark:lg:bg-gray-950 dark:lg:ring-white/10
        "
    >
      {/* Sidebar on desktop */}
      <div
        className={cn(
          'fixed inset-y-0 w-64 max-lg:hidden',
          isRTL ? 'right-0' : 'left-0'
        )}
      >
        {sidebar}
      </div>

      {/* Sidebar on mobile */}
      <MobileSidebar close={handleToggleSidebar} open={isSidebarExpanded}>
        {sidebar}
      </MobileSidebar>

      {/* Navbar on mobile */}
      <header className="flex items-center border border-pink-500 px-4 lg:hidden">
        <div className="py-2.5">
          <NavbarItem
            aria-label="Open navigation"
            onClick={handleToggleSidebar}
          >
            <OpenMenuIcon />
          </NavbarItem>
        </div>
        <div className="min-w-0 flex-1">{navbar}</div>
      </header>

      {/* Content */}
      {/* <main className="flex flex-1 flex-col pb-2 lg:min-w-0 lg:pt-2 lg:pr-2 lg:pl-64">
        <div
          className="
            grow p-6 lg:rounded-lg lg:bg-white lg:p-10 lg:shadow-xs lg:ring-1
            lg:ring-gray-950/5 dark:lg:bg-gray-900 dark:lg:ring-white/10
          "
        >
          <div className="mx-auto max-w-6xl">{children}</div>
        </div>
      </main> */}

      {/* Content ALT */}
      <main className="flex flex-1 flex-col pb-2 lg:min-w-0 lg:pe-2 lg:ps-64 lg:pt-2">
        <div className="sticky top-0 z-40 flex shrink-0">
          {headerChildren}
          {/* <header
            className="h-16 grow border border-green-200 px-6 shadow-sm
            lg:rounded-t-lg lg:bg-white lg:p-10 lg:shadow-xs lg:ring-1
             lg:ring-gray-950/5 dark:lg:bg-gray-900 dark:lg:ring-white/10
            "
          >
            {headerChildren}
          </header> */}
        </div>

        <div
          className="lg:shadow-xs grow border border-red-200
            lg:rounded-b-lg lg:bg-white lg:ring-1 lg:ring-gray-950/5
             dark:lg:bg-gray-900 dark:lg:ring-white/10
          "
        >
          <div className="mx-auto border">{children}</div>
        </div>
      </main>
    </div>
  );
}
